import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { WhatsappLogo, Check, X, ArrowClockwise, Info } from '@phosphor-icons/react';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';

const API = process.env.REACT_APP_BACKEND_URL || '';

const WEEKDAYS = [
  { idx: '0', label: 'Segunda' },
  { idx: '1', label: 'Terça' },
  { idx: '2', label: 'Quarta' },
  { idx: '3', label: 'Quinta' },
  { idx: '4', label: 'Sexta' },
  { idx: '5', label: 'Sábado' },
  { idx: '6', label: 'Domingo' },
];

const STATUS_BADGE = {
  sent: 'bg-emerald-100 text-emerald-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  read: 'bg-blue-100 text-blue-700',
  pending: 'bg-amber-100 text-amber-700',
  failed: 'bg-red-100 text-red-700',
  retried: 'bg-slate-100 text-slate-600',
};

export default function WhatsAppPage() {
  useRequireAdmin();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [settings, setSettings] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testText, setTestText] = useState('Mensagem de teste, nutrianapereira');

  const fetchAll = useCallback(async () => {
    try {
      const [s, m] = await Promise.all([
        axios.get(`${API}/api/admin/whatsapp/settings`),
        axios.get(`${API}/api/admin/whatsapp/messages?limit=100`),
      ]);
      setSettings(s.data);
      setMessages(m.data || []);
    } catch (e) {
      toast.error('Erro a carregar definições');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const updateField = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));
  const updateRule = (idx, value) => setSettings(prev => ({
    ...prev,
    reminder_timing_rules: { ...(prev.reminder_timing_rules || {}), [idx]: Number(value) || 0 },
  }));

  const save = async () => {
    if (!isAdmin) return;
    setSaving(true);
    try {
      const payload = {
        reminder_enabled: !!settings.reminder_enabled,
        feedback_enabled: !!settings.feedback_enabled,
        task_reminder_enabled: !!settings.task_reminder_enabled,
        reminder_timing_rules: settings.reminder_timing_rules,
        feedback_delay_hours: Number(settings.feedback_delay_hours) || 48,
        reminder_template_name: settings.reminder_template_name || '',
        feedback_template_name: settings.feedback_template_name || '',
        reminder_message_text: settings.reminder_message_text || '',
        feedback_message_text: settings.feedback_message_text || '',
        language_code: settings.language_code || 'pt_PT',
        use_template: !!settings.use_template,
      };
      const { data } = await axios.put(`${API}/api/admin/whatsapp/settings`, payload);
      setSettings(data);
      toast.success('Definições guardadas');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro a guardar');
    } finally {
      setSaving(false);
    }
  };

  const retry = async (id) => {
    try {
      await axios.post(`${API}/api/admin/whatsapp/messages/${id}/retry`);
      toast.success('Reenvio iniciado');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };

  const sendTest = async () => {
    if (!testPhone.trim()) { toast.error('Indica um número'); return; }
    try {
      const { data } = await axios.post(`${API}/api/admin/whatsapp/test-send`, {
        phone: testPhone.trim(), text: testText, country_code: '+351',
      });
      if (data.ok) toast.success(`Enviado para ${data.phone}`);
      else toast.error(data.error || 'Falhou');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };

  if (loading || !settings) {
    return <div className="fade-in p-8 text-brand-muted">A carregar…</div>;
  }

  return (
    <div className="fade-in space-y-6" data-testid="admin-whatsapp-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-emerald-100 flex items-center justify-center">
            <WhatsappLogo size={22} className="text-emerald-700" weight="duotone" />
          </div>
          <div>
            <h2 className="font-heading font-medium text-2xl text-brand-text">WhatsApp</h2>
            <p className="text-sm text-brand-muted">Lembretes de consulta e mensagens de feedback automáticas</p>
          </div>
        </div>
        <div className={`text-xs rounded-full px-3 py-1.5 ${settings.configured ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}
          data-testid="whatsapp-config-status">
          {settings.configured ? 'API configurada' : 'API não configurada. Define as credenciais Meta no .env'}
        </div>
      </div>

      {/* Toggles */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-brand-border rounded-2xl p-5 flex items-start justify-between gap-4">
          <div>
            <p className="font-medium text-sm text-brand-text">Lembretes de consulta</p>
            <p className="text-xs text-brand-muted mt-1">Envia lembrete automático antes da consulta segundo as regras semanais.</p>
          </div>
          <Switch
            checked={!!settings.reminder_enabled}
            onCheckedChange={(v) => isAdmin && updateField('reminder_enabled', v)}
            disabled={!isAdmin}
            data-testid="toggle-reminder-enabled"
          />
        </div>
        <div className="bg-white border border-brand-border rounded-2xl p-5 flex items-start justify-between gap-4">
          <div>
            <p className="font-medium text-sm text-brand-text">Mensagens de feedback (descontinuado)</p>
            <p className="text-xs text-brand-muted mt-1">O feedback pós-consulta é pedido por email 24 a 48 horas depois. O lembrete por WhatsApp aos 3 dias configura-se em Configurações, Acompanhamento.</p>
          </div>
          <Switch
            checked={!!settings.feedback_enabled}
            onCheckedChange={(v) => isAdmin && updateField('feedback_enabled', v)}
            disabled
            data-testid="toggle-feedback-enabled"
          />
        </div>
        <div className="bg-white border border-brand-border rounded-2xl p-5 flex items-start justify-between gap-4">
          <div>
            <p className="font-medium text-sm text-brand-text">Lembretes de tarefas em atraso</p>
            <p className="text-xs text-brand-muted mt-1">Envia diariamente ao responsável pela tarefa (equipa clínica ou colaboradores) enquanto estiver em atraso.</p>
          </div>
          <Switch
            checked={!!settings.task_reminder_enabled}
            onCheckedChange={(v) => isAdmin && updateField('task_reminder_enabled', v)}
            disabled={!isAdmin}
            data-testid="toggle-task-reminder-enabled"
          />
        </div>
      </div>

      {/* Timing rules */}
      <div className="bg-white border border-brand-border rounded-2xl p-5">
        <p className="font-medium text-sm text-brand-text mb-3">Horas antes da consulta (por dia da semana)</p>
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
          {WEEKDAYS.map(d => (
            <div key={d.idx}>
              <Label className="text-[11px] text-brand-muted">{d.label}</Label>
              <Input
                type="number" min="0" max="168"
                value={settings.reminder_timing_rules?.[d.idx] ?? 24}
                onChange={(e) => isAdmin && updateRule(d.idx, e.target.value)}
                disabled={!isAdmin}
                className="mt-1"
                data-testid={`timing-rule-${d.idx}`}
              />
            </div>
          ))}
        </div>
        <div className="flex items-start gap-2 mt-3 text-xs text-brand-muted">
          <Info size={14} className="mt-0.5 flex-shrink-0" />
          <p>Sugestão: Segunda 72h · Terça-Sexta 24h · Sábado 48h. O envio acontece à hora calculada para cada consulta.</p>
        </div>
      </div>

      {/* Feedback delay */}
      <div className="bg-white border border-brand-border rounded-2xl p-5 grid md:grid-cols-3 gap-4">
        <div>
          <Label className="text-xs text-brand-muted">Atraso para feedback (horas após consulta)</Label>
          <Input
            type="number" min="1" max="240"
            value={settings.feedback_delay_hours ?? 48}
            onChange={(e) => isAdmin && updateField('feedback_delay_hours', Number(e.target.value))}
            disabled={!isAdmin}
            className="mt-1"
            data-testid="feedback-delay-hours"
          />
        </div>
        <div>
          <Label className="text-xs text-brand-muted">Idioma do template</Label>
          <Input
            value={settings.language_code || 'pt_PT'}
            onChange={(e) => isAdmin && updateField('language_code', e.target.value)}
            disabled={!isAdmin}
            className="mt-1"
            data-testid="language-code"
          />
        </div>
        <div className="flex items-end gap-3">
          <Switch
            checked={!!settings.use_template}
            onCheckedChange={(v) => isAdmin && updateField('use_template', v)}
            disabled={!isAdmin}
            data-testid="toggle-use-template"
          />
          <div className="text-xs text-brand-muted">Usar templates aprovados pela Meta (caso contrário, envia texto livre, só funciona dentro da janela de 24h após mensagem do utilizador)</div>
        </div>
      </div>

      {/* Templates */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-brand-border rounded-2xl p-5 space-y-3">
          <div>
            <p className="font-medium text-sm text-brand-text">Mensagem de lembrete</p>
            <p className="text-[11px] text-brand-muted">Variáveis disponíveis: <code>[NOME]</code>, <code>[DATA_HORA]</code>, <code>[MODALIDADE]</code></p>
          </div>
          <Input
            value={settings.reminder_template_name || ''}
            onChange={(e) => isAdmin && updateField('reminder_template_name', e.target.value)}
            disabled={!isAdmin}
            placeholder="Nome do template Meta (se ativo)"
            data-testid="reminder-template-name"
          />
          <Textarea
            rows={10}
            value={settings.reminder_message_text || ''}
            onChange={(e) => isAdmin && updateField('reminder_message_text', e.target.value)}
            disabled={!isAdmin}
            data-testid="reminder-message-text"
          />
        </div>
        <div className="bg-white border border-brand-border rounded-2xl p-5 space-y-3">
          <div>
            <p className="font-medium text-sm text-brand-text">Mensagem de feedback</p>
            <p className="text-[11px] text-brand-muted">Variáveis disponíveis: <code>[NOME]</code></p>
          </div>
          <Input
            value={settings.feedback_template_name || ''}
            onChange={(e) => isAdmin && updateField('feedback_template_name', e.target.value)}
            disabled={!isAdmin}
            placeholder="Nome do template Meta (se ativo)"
            data-testid="feedback-template-name"
          />
          <Textarea
            rows={10}
            value={settings.feedback_message_text || ''}
            onChange={(e) => isAdmin && updateField('feedback_message_text', e.target.value)}
            disabled={!isAdmin}
            data-testid="feedback-message-text"
          />
        </div>
      </div>

      {isAdmin && (
        <div className="flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            className="bg-brand-green text-white rounded-full px-6 py-2.5 text-sm font-medium disabled:opacity-50"
            data-testid="save-whatsapp-settings"
          >
            {saving ? 'A guardar…' : 'Guardar definições'}
          </button>
        </div>
      )}

      {/* Test send */}
      {isAdmin && (
        <div className="bg-white border border-brand-border rounded-2xl p-5">
          <p className="font-medium text-sm text-brand-text mb-2">Enviar mensagem de teste</p>
          <p className="text-[11px] text-brand-muted mb-3">Envia texto livre via WhatsApp Business API. Útil para validar configuração.</p>
          <div className="grid md:grid-cols-3 gap-3">
            <Input placeholder="Telefone (ex: 916437100)" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} data-testid="test-phone" />
            <Input value={testText} onChange={(e) => setTestText(e.target.value)} data-testid="test-text" className="md:col-span-2" />
          </div>
          <button
            onClick={sendTest}
            className="mt-3 bg-emerald-600 text-white rounded-full px-5 py-2 text-sm"
            data-testid="send-test-btn"
          >
            Enviar teste
          </button>
        </div>
      )}

      {/* Message log */}
      <div className="bg-white border border-brand-border rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="font-medium text-sm text-brand-text">Registo de mensagens (últimas 100)</p>
          <button onClick={fetchAll} className="text-xs text-brand-muted flex items-center gap-1 hover:text-brand-text">
            <ArrowClockwise size={14} /> Actualizar
          </button>
        </div>
        {messages.length === 0 ? (
          <p className="text-center py-8 text-sm text-brand-muted">Ainda não há mensagens enviadas.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="messages-table">
              <thead>
                <tr className="text-left text-[11px] text-brand-muted border-b border-brand-border">
                  <th className="py-2 pr-3">Paciente</th>
                  <th className="py-2 pr-3">Tipo</th>
                  <th className="py-2 pr-3">Consulta</th>
                  <th className="py-2 pr-3">Enviado</th>
                  <th className="py-2 pr-3">Estado</th>
                  <th className="py-2 pr-3">Detalhe</th>
                  <th className="py-2 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {messages.map(m => (
                  <tr key={m.id} className="border-b border-brand-border last:border-b-0">
                    <td className="py-2 pr-3">
                      <p className="font-medium text-xs">{m.patient_name || '-'}</p>
                      <p className="text-[10px] text-brand-muted">{m.phone_number}</p>
                    </td>
                    <td className="py-2 pr-3">
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100">{m.message_type === 'reminder' ? 'Lembrete' : m.message_type === 'access_link' ? 'Acesso' : 'Feedback'}</span>
                    </td>
                    <td className="py-2 pr-3 text-xs text-brand-muted">{(m.consultation_datetime || '').replace('T', ' às ').slice(0, 16)}</td>
                    <td className="py-2 pr-3 text-xs text-brand-muted">{(m.sent_at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td className="py-2 pr-3">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_BADGE[m.status] || 'bg-slate-100 text-slate-600'}`}>
                        {m.status}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-[11px] text-red-600 max-w-[240px] truncate" title={m.error_message || ''}>
                      {m.error_message || ''}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {m.status === 'failed' && m.message_type !== 'access_link' && (
                        <button onClick={() => retry(m.id)} className="text-xs text-brand-green hover:underline" data-testid={`retry-${m.id}`}>
                          Reenviar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Setup help */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 text-sm text-amber-900">
        <p className="font-medium mb-2">Configuração inicial (Meta Developers)</p>
        <ol className="list-decimal pl-5 space-y-1 text-[13px]">
          <li>Cria a App em <code>developers.facebook.com</code> e ativa o produto WhatsApp Business.</li>
          <li>Define as variáveis de ambiente <code>WHATSAPP_ACCESS_TOKEN</code>, <code>WHATSAPP_PHONE_NUMBER_ID</code>, <code>WHATSAPP_BUSINESS_ACCOUNT_ID</code>.</li>
          <li>No campo "Webhook" do Meta, indica:
            <ul className="list-disc pl-5 mt-1">
              <li><b>Callback URL:</b> <code>{API || ''}/api/whatsapp/webhook</code></li>
              <li><b>Verify Token:</b> valor da env <code>WHATSAPP_WEBHOOK_VERIFY_TOKEN</code> (default: <code>nutriana_whatsapp_2026</code>)</li>
              <li>Subscreve aos campos: <code>messages</code>.</li>
            </ul>
          </li>
          <li>Submete os templates (<code>{settings.reminder_template_name}</code>, <code>{settings.feedback_template_name}</code>) para aprovação Meta antes de ativar "Usar templates aprovados".</li>
        </ol>
      </div>
    </div>
  );
}
